import type { SyntheticEvent } from "react";
import { useTypedSelector } from "hooks";
import { when } from "util/support";
import type { Country } from "features/general/types";
import { selectCountries } from "features/general/selectors";

export type Adapter<TKey extends keyof Country> = {
    readonly value: Country[TKey] | Array<Country[TKey]>;
    readonly onChange: (
        event: SyntheticEvent,
        values: Array<Country[TKey]>
    ) => void;
};

type UseAdapterArg<TKey extends keyof Country> = Adapter<TKey> & {
    readonly key: TKey;
    readonly multiple?: boolean;
};

export const useAdapter = <TKey extends keyof Country>({
    multiple,
    key,
    value,
    onChange
}: UseAdapterArg<TKey>) => {
    const options = useTypedSelector(selectCountries());

    const getValue = () => (
        options.filter((option: Country) => (
            [value].flat().includes(String(option[key]))
        ))
    );

    const getOptionLabel = (country: Country) => String(country[key]);

    const handleChange = (
        event: SyntheticEvent,
        countries: Country | Array<Country> | null
    ) => (
        onChange(event, [countries]
            .flat()
            .filter(Boolean)
            .map(country => String(country![key]))
        )
    );

    return {
        value: when(
            multiple,
            getValue,
            () => getValue().at(0) || null
        ),
        getValue,
        getOptionLabel,
        handleChange
    };
};

export default useAdapter;
